import React, { useCallback, useMemo, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "sonner";

import AlertDialog from "@/src/components/dialog/AlertDialog";

import {
  encodingProfileColumns,
  getEncodingProfileHref,
} from "./tableParams/encodingProfileColumns";
import EncoderAPI from "../../../api/EncoderAPI";
import CappedPage from "../../common/CappedPage";
import CreateButton from "../../common/CreateButton";
import DataTable from "../../dataTable/DataTable";
import { ENCODING_PROFILES_ROUTE } from "../../routes/routes";

import type { JSX } from "react";

import type {
  EncodingProfile,
  GetEncodingProfileListResponse,
} from "../../../api/EncoderAPI.types";
import type { AppState } from "../../../reducers";

function EncodingProfileList(): JSX.Element {
  const { t } = useTranslation("editor-encoding-profile");
  const history = useHistory();
  const queryClient = useQueryClient();
  const [defaultProfileUuid, setDefaultProfileUuid] = useState<string | null>(
    null,
  );

  const getEncodingProfileListQuery = EncoderAPI.generateQuery({
    fn: EncoderAPI.getEncodingProfileList,
    params: [],
  });

  const {
    data: encodingProfileList,
    isLoading,
    isError,
    refetch,
  } = useQuery(getEncodingProfileListQuery);

  const hasFastOption = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options?.allow_fast ??
      false,
  );

  const profileList = useMemo(
    (): EncodingProfile[] => encodingProfileList?.data ?? [],
    [encodingProfileList?.data],
  );

  const { mutate: deleteEncodingProfile } = useMutation({
    mutationFn: async (uuid: string) => EncoderAPI.deleteEncodingProfile(uuid),
    onError: () => {
      toast.error(t("error:genericError"));
    },
    onSuccess: () => {
      queryClient.invalidateQueries(getEncodingProfileListQuery.queryKey);
      toast.success(t("encodingProfileList.deleteSuccess"));
    },
  });

  const { mutate: setDefaultProfile } = useMutation({
    mutationFn: async (uuid: string) =>
      EncoderAPI.setEncodingProfileDefault(uuid),
    onMutate: async (uuid) => {
      await queryClient.cancelQueries(getEncodingProfileListQuery.queryKey);

      const previousData =
        queryClient.getQueryData<GetEncodingProfileListResponse>(
          getEncodingProfileListQuery.queryKey,
        );

      queryClient.setQueryData<GetEncodingProfileListResponse>(
        getEncodingProfileListQuery.queryKey,
        (old) =>
          old && {
            ...old,
            data: old.data.map((profile) => ({
              ...profile,
              default: profile.uuid === uuid,
            })),
          },
      );

      return { previousData };
    },
    onSuccess: () => {
      toast.success(t("encodingProfileList.saveSuccess"));
    },
    onError: (_error, _uuid, context) => {
      toast.error(t("error:genericError"));
      queryClient.setQueryData(
        getEncodingProfileListQuery.queryKey,
        context?.previousData,
      );
    },
  });

  const getRowHref = useCallback(
    (profile: EncodingProfile): string => getEncodingProfileHref(profile.uuid),
    [],
  );

  const columnList = useMemo(
    () =>
      encodingProfileColumns({
        t,
        hasFastOption,
        onEdit: (profile) => history.push(getRowHref(profile)),
        onDelete: (profile) => deleteEncodingProfile(profile.uuid),
        onChangeDefault: (profile) => setDefaultProfileUuid(profile.uuid),
      }),
    [t, hasFastOption, history, getRowHref, deleteEncodingProfile],
  );

  const defaultProfileName = profileList.find(
    (profile) => profile.uuid === defaultProfileUuid,
  )?.label;

  const handleConfirmDefault = (): void => {
    if (defaultProfileUuid) setDefaultProfile(defaultProfileUuid);
    setDefaultProfileUuid(null);
  };

  const handleOpenChange = (isOpen: boolean): void => {
    if (!isOpen) setDefaultProfileUuid(null);
  };

  const createButton = (
    <CreateButton
      href={`${ENCODING_PROFILES_ROUTE}/new`}
      label={t("encodingProfileList.createEncodingProfile")}
    />
  );

  return (
    <CappedPage contentClassName="py-15">
      <DataTable
        columns={columnList}
        data={profileList}
        getRowHref={getRowHref}
        headerActions={createButton}
        isError={isError}
        isLoading={isLoading}
        onRetry={refetch}
        title={t("encodingProfiles")}
      />
      <AlertDialog
        cancelLabel={t("table:dataTable.cancel")}
        confirmLabel={t("encodingProfileList.replace")}
        isOpen={defaultProfileUuid !== null}
        onConfirm={handleConfirmDefault}
        onOpenChange={handleOpenChange}
        title={t("encodingProfileList.changeDefaultProfile")}
        description={t("encodingProfileList.changeDefaultProfileText", {
          profileName: defaultProfileName,
        })}
      />
    </CappedPage>
  );
}

export default EncodingProfileList;
